import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Container,
  Grid,
  GridCol,
  Group,
  Modal,
  Textarea,
  TextInput,
  useMantineColorScheme
} from '@mantine/core';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import { updateApmEquipmentDetails } from '../../../libraries/apm';
import ModalComponent from '../modal';
import { isZarphSuccessResponse, useStatusOperDialog } from '../../context/status-oper-dialog';
import { StatusType } from '../../../libraries/zps';
import { IconEdit } from '@tabler/icons-react';
import './styles.scss';
interface EditEquipments {
  posid: number;
  timeopenclose?: number;
  description: string;
  update: () => void;
}

const EditEquipmentPos: React.FC<EditEquipments> = ({ posid, timeopenclose, description: propDescription, update }) => {
  const controller = useRef(new AbortController()).current;
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const { colorScheme } = useMantineColorScheme();

  const [minutes, setMinutes] = useState<number>(0);
  const [hours, setHours] = useState<number>(0);
  const [description, setDescription] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);

  const { showStatusOperDialog, handleApiError } = useStatusOperDialog();

  useEffect(() => {
    setDescription(propDescription || '');
    if (timeopenclose) {
      const hoursValue = Math.floor(timeopenclose / 60);
      const minutesValue = timeopenclose % 60;
      setHours(hoursValue);
      setMinutes(minutesValue);
    } else {
      setHours(0);
      setMinutes(0);
    }
  }, [propDescription, timeopenclose]);

  const openModal = () => {
    if (!showModal) setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    controller.abort();
  };

  const convertToMinutes = () => {
    return hours * 60 + minutes;
  };

  const handleSave = async () => {
    try {
      const totalMinutes = convertToMinutes();

      const response = await updateApmEquipmentDetails(posid, totalMinutes, description, controller);

      if (!isZarphSuccessResponse(response)) throw response;

      showStatusOperDialog({
        type: StatusType.INFO,
        message: lang.EDIT_EQUIPMENTS_ALERT_UPDATED_SUCCESS || '',
        statusOper: response?.data?.statusOper
      });

      update();
      closeModal();
    } catch (error: any) {
      handleApiError(error, lang.EDIT_EQUIPMENTS_ALERT_UPDATED_ERROR);
    }
  };

  const header = (
    <div>
      <Modal.Title>{lang.EQUIPMENT_EDIT}</Modal.Title>
      <div className="header-details">
        <span>{`${posid} / `}</span>
        <span>{`${description}`}</span>
      </div>
    </div>
  );

  return (
    <>
      <IconEdit stroke={2} onClick={openModal} />

      <ModalComponent
        show={showModal}
        handleClick={closeModal}
        title={header}
        content={
          <Container bg={colorScheme === 'dark' ? '#1b1b1b' : 'white'} classNames={{ root: 'edit-equipment' }}>
            <Grid>
              <GridCol>
                <h3>{lang.EQUIPMENT_AUTOMATIC_CLOSE}</h3>
              </GridCol>
            </Grid>

            <Group classNames={{ root: 'hours-minutes' }}>
              <TextInput
                label={lang.GLOBAL_HOURS}
                value={hours}
                type="number"
                onChange={e => setHours(parseInt(e.target.value))}
              />
              <TextInput
                label={lang.GLOBAL_MINUTES}
                value={minutes}
                type="number"
                onChange={e => setMinutes(parseInt(e.target.value))}
              />
            </Group>

            <Group classNames={{ root: 'description' }}>
              <Textarea
                label={lang.EQUIPMENT_DESCRIPTION}
                value={description}
                id="description"
                name="description"
                onChange={e => setDescription(e.target.value)}
              />
            </Group>

            <Group>
              <Button color="#538539" radius="md" onClick={handleSave}>
                {lang.GLOBAL_SAVE_BUTTON}
              </Button>
            </Group>
          </Container>
        }
      />
    </>
  );
};

export default EditEquipmentPos;
